import USBDevice from "../Models/UsbDevice";
import { Firmware, FlashMode } from "../Types";
import Notify from "../Utils/Notify";
import Pattern from "../Models/Pattern";
import { parseIntelHex } from "../Utils/IntelHex";
import { calculateblocks, getEraseSectorCount } from "../Utils/FirmwareBlocker";
import { Buffer } from 'buffer';

type QueueTask = () => Promise<any>;

class WebusbManager {

    connectedDevices: string[] = [];
    selectedDevice: any;
    selectedDeviceId: string = '';
    selectedDeviceType: number = 0;
    seqno = { value: 0x11 };
    private taskQueue: QueueTask[] = [];
    private isProcessingQueue = false;

    usbDevice: USBDevice | null = null;
    logger: Function | null;

    spoof: any = new URLSearchParams(window.location.search).has("spoof");
    verbose: boolean = new URLSearchParams(window.location.search).has("verbose");

    constructor(logFunction: Function | null = null) {
        this.logger = logFunction
    }

    enqueue<T>(task: () => Promise<T>): Promise<T> {
        return new Promise((resolve, reject) => {
            this.taskQueue.push(async () => {
                try {
                    const result = await task();
                    resolve(result);
                } catch (err) {
                    reject(err);
                }
            });
            this.processQueue();
        });
    }

    // Process the queue sequentially
    private async processQueue() {
        if (this.isProcessingQueue) return; // already processing
        this.isProcessingQueue = true;

        while (this.taskQueue.length > 0) {
            const task = this.taskQueue.shift();
            if (!task) continue;

            try {
                await task(); // wait for completion
            } catch (error) {
                console.error("Queue task failed:", error);
            }
        }

        this.isProcessingQueue = false;
    }

    constructUSBDevice(device: any) {
        return new USBDevice(device, this.logger, this.seqno, this.verbose);
    }

    registerEvent(name: string, callback: Function) {
        navigator.usb.addEventListener(name, (e: any) => callback(e));
    }

    clear() {
        this.selectedDevice = null;
        this.selectedDeviceId = '';
        this.selectedDeviceType = 0;
    }

    setDeviceData(USB: USBDevice) {
        if(USB.SERIAL_NUM) {
            this.connectedDevices.push(USB.SERIAL_NUM.toString());
            this.selectedDeviceId = USB.SERIAL_NUM.toString() ?? '';
        }
        this.selectedDeviceType = USB.DEVICE_TYPE_ID ?? 78;
        this.usbDevice = USB;
    }

    async getDevices(testMode: boolean = false) {
        return this.enqueue(async () => {
            if(this.spoof) {
                return true;
            }

            return navigator.usb.requestDevice({ 
                filters: [		
                    { 'vendorId': 0x10C4, 'productId': 0x1AE0 },
                    { 'vendorId': 0x10C4, 'productId': 0x1A16 },
                    { 'vendorId': 0x10C4, 'productId': 0x1A9A },
                ]
            })
            .then(async (device: any) => {
                const USB = this.constructUSBDevice(device)
                this.selectedDevice = device;
                await USB.interrogate()
                .then(() => {
                    this.setDeviceData(USB)
                })
                .catch((error: any) => {
                    Notify("error", "Somthing went wrong, please try again");
                    console.warn(error);
                    return false;
                });

                if(testMode) return USB.getDevDetails();
                else return true;
            })
            .catch((error: any) => {
                if(error.message.includes('Unable to claim interface'))
                    Notify("error", "Device is already in use in another tab or browser");
                console.error(error);
                return false;
            });
        });
    }

    async updateDeviceList() {
        return this.enqueue(async () => {
            const devices = await navigator.usb.getDevices();
            const deviceList: any = [];
            for (const device of devices) {
                const USB = this.constructUSBDevice(device)
                await USB.interrogate()
                .then(() => {
                    if(USB.SERIAL_NUM) deviceList.push(USB.SERIAL_NUM.toString())
                })
                .catch((error) => console.warn(error));
            }
            return this.connectedDevices = deviceList;
        });
    }

    async setSelected() {
        return this.enqueue(async () => {
            const devices = await navigator.usb.getDevices();
            for (const device of devices) {
                const USB = this.constructUSBDevice(device)
                await USB.interrogate()
                .then(() => {
                    this.selectedDevice = device;
                    this.setDeviceData(USB)
                })
                .catch((error) => console.warn(error));
            }
        });
    }

    isDeviceConnected(serialNumber: string) {
        return this.spoof ? this.spoof : this.connectedDevices.some((deviceSerial: any) => deviceSerial === serialNumber);
    }
 
    async writeData(modes: FlashMode[], settings: any) {
        return this.enqueue(async () => {
            // Create new pattern object
            const blocks = (new Pattern(modes, this.selectedDeviceType, settings)).getBlocks();

            if(this.verbose) console.log(modes);
            const deepClone = JSON.parse(JSON.stringify(blocks));
            if(this.verbose) console.log(deepClone);
            
            return await this.usbDevice?.updateDevice(blocks);
        });
    }

    async updateFirmware(firmware: Firmware, setUploadProgress: Function) {
        return this.enqueue(async () => { return this.usbDevice?.uploadFirmware(firmware, setUploadProgress)});
    }

    async readFirmwareVersion(chip_id: number) {
        return this.enqueue(async () => {
            const memoryAddresses: any = {
                73: 0x17FD,
                82: 0x02B0
            }

            const result = await this.readMemory(memoryAddresses[chip_id]);
            if (result && typeof result == 'object' && 'dataList' in result && Array.isArray(result.dataList)) {
                return result.dataList[0];
            } else {
                // console.log(result?.errorMessage || "Failed to read firmware version");
                return null;
            }
        });
    }

    async readMemory(startAdd: number, endAdd?: number) {
        if(!this.usbDevice) return { errorMessage: 'no USB Device' };
        return await this.usbDevice?.readMemory(startAdd, endAdd ?? startAdd);
    }

    async erasePage(page: number) {
        return this.enqueue(async () => {
            if(!this.usbDevice) return;
            const keysResponse = await this.usbDevice.setKeys();
            if(!keysResponse.isSuccess) {
                this.usbDevice.log(keysResponse.message);
                return false;
            }
            return await this.usbDevice.erase(page * 512);
        });
    }

    async parseFirmware(payload: any, chip_id: number) {
        try {
            const arrayBuffer = await payload.arrayBuffer();
            const unencryptedfirmware = parseIntelHex(Buffer.from(arrayBuffer), 65536, 0);
            if(!this.usbDevice?.checkFirmwareProductId(unencryptedfirmware)) {
                console.log("Not Passed Check Firmware")
                return null;
            }
            
            const blocks = calculateblocks(unencryptedfirmware, chip_id);
            if(!blocks) return false;
            const eraseblocks = getEraseSectorCount(chip_id);

            return { memorypayload: blocks.list, eraseblocks, endAddress: blocks.endAddress };
        } catch(e) {
            console.log(e);
            return false;
        }  
    }

    async integrityCheck(payload: any) {
        return this.enqueue(async () => {
            if(!payload || !payload.memorypayload) return true;
            const lastBlock = [...payload.memorypayload[payload.memorypayload.length - 1].data].map((b: number) => (b ^ 0x15));

            const memory: any = (await this.usbDevice?.getDataRange(payload.endAddress - lastBlock.length, payload.endAddress - 1))
                .map((m: any) => m.value)

            try {
                if(!this.arraysEqual(lastBlock, memory)) {
                    console.error("Failed Integry Check");
                    console.log("Device: " + this.toHexString(memory));
                    console.log("File: " + this.toHexString(lastBlock));
                    return false;
                }
            } catch (error) {
                console.log(memory)
                console.log(lastBlock)
                console.log("Failed Checking firmware", error) 
            }
        
            return true;
        });
    }
    
    arraysEqual(a: number[], b: number[]) {
        if (a.length !== b.length) return false;

        for (let i = 0; i < a.length; i++) {
            if (a[i] !== b[i]) return false;
        }

        return true;
    }

    toHexString(data: number[]) {
        return data.map((d: number) => d.toString(16).padStart(2, '0').toUpperCase()).join(" ")
    }
}



export default WebusbManager;